import { useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ContentBody from "./ContentBody";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_START = "2026-05-01";

const km = (meters) => (meters / 1000).toFixed(1);
const dur = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
};
const dateStr = (iso) => new Date(iso).toLocaleDateString("nl-NL", {
  day: "numeric", month: "short", year: "numeric"
});

function SortHeader({ field, sortBy, sortDir, onSort, children, col }) {
  const active = sortBy === field;
  return (
    <th className={`sortable-header${active ? " sort-active" : ""}${col ? ` col-${col}` : ""}`} onClick={() => onSort(field)}>
      {children}{active ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );
}

export default function Athletes() {
  const { athleteId } = useParams();

  const [athletes, setAthletes] = useState([]);
  const [stats, setStats] = useState([]);
  const [donationTotals, setDonationTotals] = useState([]);
  const [content, setContent] = useState(null);
  const [sortBy, setSortBy] = useState("donationTotal");
  const [sortDir, setSortDir] = useState("desc");

  const [selectedActivities, setSelectedActivities] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const activityCache = useRef({});
  const detailRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/athletes-list`)
      .then(r => r.json())
      .then(data => setAthletes(Array.isArray(data) ? data : []));
    fetch(`${API}/athlete-stats`)
      .then(r => r.json())
      .then(data => setStats(Array.isArray(data) ? data : []));
    fetch(`${API}/donation-totals`)
      .then(r => r.json())
      .then(data => setDonationTotals(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API}/api/content/deelnemers`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setContent(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!athleteId) { setSelectedActivities([]); return; }

    const cacheKey = `${athleteId}-${showAll ? "all" : "filtered"}`;
    if (activityCache.current[cacheKey]) {
      setSelectedActivities(activityCache.current[cacheKey]);
      return;
    }

    setLoadingDetail(true);
    const url = showAll
      ? `${API}/activities?athleteId=${athleteId}`
      : `${API}/activities?athleteId=${athleteId}&after=${EVENT_START}`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        const activities = Array.isArray(data) ? data : [];
        activityCache.current[cacheKey] = activities;
        setSelectedActivities(activities);
      })
      .catch(console.error)
      .finally(() => setLoadingDetail(false));
  }, [athleteId, showAll]);

  useEffect(() => {
    if (athleteId && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedActivities]);

  const leaderboard = useMemo(() => {
    const statsMap = Object.fromEntries(stats.map(s => [s._id, s]));
    const donationsMap = Object.fromEntries(donationTotals.map(d => [d._id, d.total]));
    return athletes.map(a => ({
      athlete: a,
      trainingKm: statsMap[a._id]?.trainingKm || 0,
      eventKm: statsMap[a._id]?.eventKm || 0,
      trainingCount: statsMap[a._id]?.trainingCount || 0,
      eventCount: statsMap[a._id]?.eventCount || 0,
      donationTotal: donationsMap[a._id] || 0
    }));
  }, [athletes, stats, donationTotals]);

  const sorted = useMemo(() => {
    return [...leaderboard].sort((a, b) => {
      let diff;
      if (sortBy === "name") {
        const na = a.athlete.displayName || a.athlete.name || "";
        const nb = b.athlete.displayName || b.athlete.name || "";
        diff = na.localeCompare(nb, "nl");
      } else {
        diff = (b[sortBy] || 0) - (a[sortBy] || 0);
      }
      return sortDir === "asc" ? -diff : diff;
    });
  }, [leaderboard, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortDir("desc"); }
  };

  const selectedAthlete = athletes.find(a => a._id === athleteId);
  const trainings = selectedActivities.filter(a => a.isTraining);
  const events = selectedActivities.filter(a => !a.isTraining);

  return (
    <div className="pane">
      <h1>{content?.title || "Atleten"}</h1>
      <ContentBody data={content} />

      {/* Mobiele sorteer-control */}
      <div className="leaderboard-mobile-sort">
        <select value={sortBy} onChange={e => { setSortBy(e.target.value); setSortDir("desc"); }}>
          <option value="donationTotal">Donaties</option>
          <option value="eventKm">Evenement km</option>
          <option value="trainingKm">Training km</option>
          <option value="name">Naam</option>
        </select>
        <button className="btn-secondary" onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}>
          {sortDir === "desc" ? "↓" : "↑"}
        </button>
      </div>

      {/* Tabel-klasse bepaalt welke kolom op mobiel zichtbaar is */}
      <table className={`leaderboard leaderboard-sort-${sortBy}`}>
        <thead>
          <tr>
            <SortHeader field="name" col="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Atleet</SortHeader>
            <SortHeader field="eventKm" col="eventKm" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Evenement km</SortHeader>
            <SortHeader field="trainingKm" col="trainingKm" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Training km</SortHeader>
            <SortHeader field="donationTotal" col="donationTotal" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Donaties</SortHeader>
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ athlete, trainingKm, eventKm, trainingCount, eventCount, donationTotal }) => (
            <tr key={athlete._id} className={athlete._id === athleteId ? "leaderboard-selected" : ""}>
              <td className="col-name">
                <Link to={`/deelnemers/${athlete.slug || athlete._id}`} className="leaderboard-athlete">
                  {athlete.profilePhoto && (
                    <img src={athlete.profilePhoto} alt={athlete.firstName} className="leaderboard-photo" />
                  )}
                  {athlete.displayName || athlete.name}
                </Link>
              </td>
              <td className="leaderboard-num col-eventKm">{eventCount > 0 ? `${km(eventKm)} km (${eventCount})` : "-"}</td>
              <td className="leaderboard-num col-trainingKm">{trainingCount > 0 ? `${km(trainingKm)} km (${trainingCount})` : "-"}</td>
              <td className="leaderboard-num col-donationTotal">{donationTotal > 0 ? `€ ${donationTotal.toLocaleString("nl-NL")}` : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {athleteId && selectedAthlete && (
        <div className="athlete-detail" ref={detailRef}>
          <div className="athlete-detail-header">
            {selectedAthlete.profilePhoto && (
              <img src={selectedAthlete.profilePhoto} alt={selectedAthlete.firstName} className="athlete-detail-photo" />
            )}
            <div>
              <h2>{selectedAthlete.displayName || selectedAthlete.name}</h2>
              <div className="athlete-stats">
                <div className="athlete-stat">
                  <span className="stat-value">{selectedActivities.length}</span>
                  <span className="stat-label">activiteiten</span>
                </div>
                <div className="athlete-stat">
                  <span className="stat-value">{km(selectedActivities.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                  <span className="stat-label">km totaal</span>
                </div>
                {events.length > 0 && (
                  <div className="athlete-stat">
                    <span className="stat-value">{km(events.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                    <span className="stat-label">km evenementen ({events.length})</span>
                  </div>
                )}
                {trainings.length > 0 && (
                  <div className="athlete-stat">
                    <span className="stat-value">{km(trainings.reduce((s, a) => s + (a.distance || 0), 0))}</span>
                    <span className="stat-label">km trainingen ({trainings.length})</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="detail-filter-bar">
            {!showAll ? (
              <p className="note">
                Activiteiten vanaf {new Date(EVENT_START).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}.{" "}
                <button className="btn-link" onClick={() => setShowAll(true)}>
                  Toon alle activiteiten (kan trager laden)
                </button>
              </p>
            ) : (
              <p className="note">
                Alle activiteiten worden getoond.{" "}
                <button className="btn-link" onClick={() => setShowAll(false)}>Toon alleen actieperiode</button>
              </p>
            )}
          </div>

          {loadingDetail && <p>Activiteiten laden...</p>}

          {!loadingDetail && events.length > 0 && (
            <section>
              <h3>Evenement activiteiten</h3>
              <table className="activity-table">
                <tbody>
                  {events.map(a => (
                    <tr key={a._id}>
                      <td>{a.name}</td>
                      <td>{dateStr(a.date)}</td>
                      <td>{km(a.distance)} km</td>
                      <td>{dur(a.moving_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {!loadingDetail && trainings.length > 0 && (
            <section>
              <h3>Trainingen</h3>
              <table className="activity-table">
                <tbody>
                  {trainings.map(a => (
                    <tr key={a._id}>
                      <td>{a.name}</td>
                      <td>{dateStr(a.date)}</td>
                      <td>{km(a.distance)} km</td>
                      <td>{dur(a.moving_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {!loadingDetail && selectedActivities.length === 0 && (
            <p className="note">Geen activiteiten gevonden voor deze periode.</p>
          )}
        </div>
      )}
    </div>
  );
}
